import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from '@nexdel/core/utils/icons';
import { api, ApiError } from '@nexdel/core/api';
import {
  PageShell,
  ContextCard,
  SubmittedFeedbackCard,
  FeedbackForm,
  ThanksState,
  type FeedbackContext,
} from './feedback/FeedbackForm';

interface PublicFeedbackContext extends FeedbackContext {
  expiresAt: string;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; httpStatus: number; message: string }
  | { status: 'ready'; ctx: PublicFeedbackContext };

export default function ExternalFeedback() {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [submittedJustNow, setSubmittedJustNow] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const ctx = await api.get<PublicFeedbackContext>(`/api/public/feedback/${token}`);
        if (!cancelled) setState({ status: 'ready', ctx });
      } catch (err) {
        if (cancelled) return;
        const httpStatus = err instanceof ApiError ? err.status : 0;
        const message = err instanceof Error ? err.message : 'Failed to load feedback request';
        setState({ status: 'error', httpStatus, message });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.status === 'loading') {
    return <PageShell><div className="text-sm text-muted-foreground">{t('common.loading')}</div></PageShell>;
  }

  if (state.status === 'error') {
    return (
      <PageShell>
        <ErrorState httpStatus={state.httpStatus} message={state.message} t={t} />
      </PageShell>
    );
  }

  const { ctx } = state;
  const fmtDateTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  if (ctx.alreadySubmitted && !submittedJustNow) {
    return (
      <PageShell>
        <SubmittedState ctx={ctx} fmtDateTime={fmtDateTime} t={t} />
      </PageShell>
    );
  }

  if (submittedJustNow) {
    return (
      <PageShell>
        <ThanksState
          ctx={ctx}
          title={t('externalFeedback.thanksTitle')}
          body={t('externalFeedback.thanksBody')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <FeedbackForm
        ctx={ctx}
        submitUrl={`/api/public/feedback/${token}`}
        method="POST"
        fmtDateTime={fmtDateTime}
        onSubmitted={() => setSubmittedJustNow(true)}
        t={t}
        title={t('externalFeedback.title')}
        intro={t('externalFeedback.intro')}
        contextLabel={t('externalFeedback.contextLabel')}
        submitButton={t('externalFeedback.submitButton')}
        footerHint={t('externalFeedback.singleUseHint')}
      />
    </PageShell>
  );
}

// ── External-specific subviews ──────────────────────────────────────────────

function ErrorState({ httpStatus, message, t }: {
  httpStatus: number;
  message: string;
  t: (k: string) => string;
}) {
  const isGone = httpStatus === 410;
  const isNotFound = httpStatus === 404;
  return (
    <div className="text-center space-y-3 py-6">
      <XCircle className="mx-auto text-destructive" size={36} />
      <h1 className="text-lg font-semibold text-foreground">
        {isGone ? t('externalFeedback.expiredTitle')
          : isNotFound ? t('externalFeedback.notFoundTitle')
          : t('externalFeedback.errorTitle')}
      </h1>
      <p className="text-sm text-muted-foreground">
        {isGone ? t('externalFeedback.expiredBody')
          : isNotFound ? t('externalFeedback.notFoundBody')
          : message}
      </p>
    </div>
  );
}

function SubmittedState({ ctx, fmtDateTime, t }: {
  ctx: PublicFeedbackContext;
  fmtDateTime: (iso: string | null) => string;
  t: (k: string) => string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="text-emerald-600" size={20} />
        <h1 className="text-base font-semibold text-foreground">
          {t('externalFeedback.alreadySubmittedTitle')}
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('externalFeedback.alreadySubmittedBody')}
      </p>
      <ContextCard ctx={ctx} fmtDateTime={fmtDateTime} label={t('externalFeedback.contextLabel')} />
      {ctx.submittedFeedback && <SubmittedFeedbackCard f={ctx.submittedFeedback} t={t} />}
    </div>
  );
}
